/**
 * Command Bus - Typed, auditable command dispatch with middleware pipeline.
 *
 * Governs all intent-bearing operations in the Nuvra platform. Every command:
 *   1. Is validated against its registered schema
 *   2. Passes through the middleware pipeline (rate-limit, auth, audit)
 *   3. Is dispatched to exactly one registered handler
 *   4. Produces a typed CommandResult
 *   5. Emits lifecycle events on the NuvraEvent bus
 *
 * Design invariants:
 *   - Exactly one handler per command type (conflict throws at registration time)
 *   - Middleware pipeline is ordered and fail-closed
 *   - All commands carry a stable commandId for correlation
 *   - No handler registered = rejected (not silently dropped)
 */

import { logger }   from "@/system/logger";
import { eventBus } from "@/runtime/eventBus";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Command<P = unknown> {
  commandId:   string;
  commandType: string;
  payload:     P;
  source:      string;
  issuedAt:    number;
  correlationId?: string;
}

export type CommandStatus = "pending" | "running" | "completed" | "failed" | "rejected";

export interface CommandResult<R = unknown> {
  commandId:   string;
  commandType: string;
  status:      CommandStatus;
  result?:     R;
  error?:      string;
  durationMs:  number;
}

export type CommandHandler<P = unknown, R = unknown> = (
  cmd: Command<P>,
) => R | Promise<R>;

export type CommandMiddleware = (
  cmd:  Command,
  next: () => Promise<CommandResult>,
) => Promise<CommandResult>;

export interface CommandRegistration<P = unknown, R = unknown> {
  commandType: string;
  handler:     CommandHandler<P, R>;
  label:       string;
}

// ── Error types ───────────────────────────────────────────────────────────────

export class CommandHandlerConflictError extends Error {
  constructor(commandType: string, existing: string) {
    super(
      `Command "${commandType}" already has a registered handler ("${existing}"). ` +
      `Unregister the existing handler before registering a new one.`,
    );
    this.name = "CommandHandlerConflictError";
  }
}

export class CommandNotRegisteredError extends Error {
  constructor(commandType: string) {
    super(`No handler registered for command type "${commandType}".`);
    this.name = "CommandNotRegisteredError";
  }
}

// ── Middleware helpers ─────────────────────────────────────────────────────────

const MAX_INFLIGHT = 200;

/** Built-in audit middleware: records every command invocation in the timeline. */
const auditMiddleware: CommandMiddleware = async (cmd, next) => {
  const result = await next();
  logger.debug("CommandBus audit", {
    commandId:   cmd.commandId,
    commandType: cmd.commandType,
    status:      result.status,
    durationMs:  result.durationMs,
    source:      cmd.source,
  });
  return result;
};

// ── Command Bus implementation ────────────────────────────────────────────────

class CommandBusImpl {
  private handlers    = new Map<string, { handler: CommandHandler; label: string }>();
  private middleware: CommandMiddleware[] = [auditMiddleware];
  private inflight    = new Map<string, Command>();

  /**
   * Register a handler for a command type.
   * Throws CommandHandlerConflictError if one is already registered.
   * Returns an unregister function.
   */
  register<P, R>(reg: CommandRegistration<P, R>): () => void {
    const existing = this.handlers.get(reg.commandType);
    if (existing) {
      throw new CommandHandlerConflictError(reg.commandType, existing.label);
    }
    this.handlers.set(reg.commandType, {
      handler: reg.handler as CommandHandler,
      label:   reg.label,
    });
    logger.debug("CommandBus registered", { commandType: reg.commandType, label: reg.label });
    return () => {
      this.handlers.delete(reg.commandType);
      logger.debug("CommandBus unregistered", { commandType: reg.commandType });
    };
  }

  /**
   * Add a middleware to the pipeline (inserted before audit middleware).
   * Returns a remove function.
   */
  use(mw: CommandMiddleware): () => void {
    this.middleware.unshift(mw);
    return () => {
      const idx = this.middleware.indexOf(mw);
      if (idx !== -1) this.middleware.splice(idx, 1);
    };
  }

  /**
   * Dispatch a command through the full pipeline.
   * Fail-closed: missing handler -> CommandNotRegisteredError wrapped in rejected result.
   */
  async dispatch<P = unknown, R = unknown>(
    cmd: Command<P>,
  ): Promise<CommandResult<R>> {
    if (this.inflight.size >= MAX_INFLIGHT) {
      const result: CommandResult<R> = {
        commandId:   cmd.commandId,
        commandType: cmd.commandType,
        status:      "rejected",
        error:       "Command bus at capacity - too many in-flight commands",
        durationMs:  0,
      };
      eventBus.emit({ type: "command:rejected", commandId: cmd.commandId, commandType: cmd.commandType, reason: "at_capacity" });
      return result;
    }

    this.inflight.set(cmd.commandId, cmd as Command);
    eventBus.emit({ type: "command:dispatched", commandId: cmd.commandId, commandType: cmd.commandType, source: cmd.source });

    const start = Date.now();

    try {
      const execute = (): Promise<CommandResult<R>> => this._runHandler<P, R>(cmd, start);

      // Build pipeline from middleware array (last added wraps first)
      let chain: () => Promise<CommandResult<R>> = execute;
      for (const mw of this.middleware) {
        const prev = chain;
        chain = () => mw(cmd as Command, prev as () => Promise<CommandResult>) as Promise<CommandResult<R>>;
      }

      const result = await chain();
      if (result.status === "completed") {
        eventBus.emit({ type: "command:completed", commandId: cmd.commandId, commandType: cmd.commandType, durationMs: result.durationMs });
      } else if (result.status === "failed") {
        eventBus.emit({ type: "command:failed", commandId: cmd.commandId, commandType: cmd.commandType, error: result.error ?? "unknown" });
      }
      return result;
    } finally {
      this.inflight.delete(cmd.commandId);
    }
  }

  private async _runHandler<P, R>(cmd: Command<P>, start: number): Promise<CommandResult<R>> {
    const reg = this.handlers.get(cmd.commandType);
    if (!reg) {
      const err = new CommandNotRegisteredError(cmd.commandType);
      eventBus.emit({ type: "command:rejected", commandId: cmd.commandId, commandType: cmd.commandType, reason: "no_handler" });
      return {
        commandId:   cmd.commandId,
        commandType: cmd.commandType,
        status:      "rejected",
        error:       err.message,
        durationMs:  Date.now() - start,
      };
    }

    try {
      const result = await reg.handler(cmd as Command);
      return {
        commandId:   cmd.commandId,
        commandType: cmd.commandType,
        status:      "completed",
        result:      result as R,
        durationMs:  Date.now() - start,
      };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error("CommandBus handler error", { commandType: cmd.commandType, error: msg });
      return {
        commandId:   cmd.commandId,
        commandType: cmd.commandType,
        status:      "failed",
        error:       msg,
        durationMs:  Date.now() - start,
      };
    }
  }

  /** Active in-flight command count. */
  getInflightCount(): number { return this.inflight.size; }

  /** List all registered command types. */
  getRegisteredTypes(): string[] { return [...this.handlers.keys()]; }

  /** True if a handler is registered for the given type. */
  hasHandler(commandType: string): boolean { return this.handlers.has(commandType); }
}

export const commandBus = new CommandBusImpl();

/**
 * Convenience factory: build a typed Command with a fresh id and current timestamp.
 */
export function createCommand<P = unknown>(
  commandType: string,
  payload:     P,
  source:      string,
  opts?: { correlationId?: string },
): Command<P> {
  return {
    commandId:     crypto.randomUUID(),
    commandType,
    payload,
    source,
    issuedAt:      Date.now(),
    correlationId: opts?.correlationId,
  };
}
